import type { ReactNode } from 'react';

export interface EventTypeConfig {
  label: string;
  category: string;
  symbol: string;
  color: 'emerald' | 'red' | 'amber' | 'blue' | 'zinc';
  description: string;
  renderDetails?: (object: unknown) => ReactNode;
  renderSummary?: (object: unknown) => string;
}

type AnyObj = Record<string, any>;

function money(obj: AnyObj | undefined, key = 'amount_money'): string {
  const m = obj?.[key];
  if (!m) return '';
  const amount = (m.amount || 0) / 100;
  const currency = m.currency || 'USD';
  return `${currency} ${amount.toFixed(2)}`;
}

export const EVENT_TYPE_REGISTRY: Record<string, EventTypeConfig> = {
  'payment.created': {
    label: 'Payment Created',
    category: 'Payments',
    symbol: '+',
    color: 'blue',
    description: 'A new payment was initiated',
    renderSummary: (obj) => {
      const payment = (obj as AnyObj)?.payment;
      return `${money(payment)} \u2014 ${payment?.status || 'unknown'}`;
    },
  },
  'payment.updated': {
    label: 'Payment Updated',
    category: 'Payments',
    symbol: '\u21BB',
    color: 'emerald',
    description: 'A payment status changed',
    renderSummary: (obj) => {
      const payment = (obj as AnyObj)?.payment;
      return `${money(payment)} \u2014 ${payment?.status || 'unknown'}`;
    },
  },
  'refund.created': {
    label: 'Refund Created',
    category: 'Refunds',
    symbol: '\u2190',
    color: 'amber',
    description: 'A refund was initiated',
    renderSummary: (obj) => {
      const refund = (obj as AnyObj)?.refund;
      return `Refund ${money(refund)} \u2014 ${refund?.status || 'pending'}`;
    },
  },
  'refund.updated': {
    label: 'Refund Updated',
    category: 'Refunds',
    symbol: '\u21BB',
    color: 'amber',
    description: 'A refund status changed',
    renderSummary: (obj) => {
      const refund = (obj as AnyObj)?.refund;
      return `Refund ${money(refund)} \u2014 ${refund?.status || 'pending'}`;
    },
  },
};

export function getEventConfig(eventType: string): EventTypeConfig {
  const existing = EVENT_TYPE_REGISTRY[eventType];
  if (existing) return existing;

  const category = eventType.split('.')[0] || 'other';
  return {
    label: eventType,
    category: category.charAt(0).toUpperCase() + category.slice(1),
    symbol: '?',
    color: 'zinc',
    description: 'Event type not yet recognized in BonesPay',
  };
}

export function getEventCategories(): string[] {
  const categories = new Set<string>();
  Object.values(EVENT_TYPE_REGISTRY).forEach((config) => categories.add(config.category));
  return Array.from(categories).sort();
}

export function getKnownEventTypes(): string[] {
  return Object.keys(EVENT_TYPE_REGISTRY).sort();
}
